const WEBM_OPUS_MIME = 'audio/webm;codecs=opus';

export function isStreamableContainer(mimeType: string | undefined): boolean {
  if (!mimeType) return false;
  const base = mimeType.split(';')[0].trim().toLowerCase();
  return (
    base === 'audio/webm' || base === 'video/webm' || base === 'audio/ogg' || base === 'video/ogg'
  );
}

const CHUNK_INTERVAL_MS = 250;

export async function streamFileAsWebm(
  file: File,
  sendChunk: (chunk: Blob) => Promise<void>
): Promise<void> {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported(WEBM_OPUS_MIME)) {
    throw new Error('WebM/Opus encoding is not supported in this environment');
  }

  const audioContext = new AudioContext();

  let audioBuffer: AudioBuffer;
  try {
    const arrayBuffer = await file.arrayBuffer();
    audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
  } catch {
    await audioContext.close();
    throw new Error('Could not decode the selected audio file');
  }

  const destination = audioContext.createMediaStreamDestination();
  const source = audioContext.createBufferSource();
  source.buffer = audioBuffer;
  source.connect(destination);

  const recorder = new MediaRecorder(destination.stream, { mimeType: WEBM_OPUS_MIME });

  let sendChain: Promise<void> = Promise.resolve();
  let sendError: unknown = null;
  recorder.ondataavailable = (event: BlobEvent) => {
    if (event.data.size === 0) return;
    sendChain = sendChain
      .then(() => sendChunk(event.data))
      .catch((error: unknown) => {
        sendError ??= error;
      });
  };

  const finished = new Promise<void>((resolve, reject) => {
    recorder.onstop = () => resolve();
    recorder.onerror = () => reject(new Error('Audio transcoding failed'));
  });

  source.onended = () => recorder.stop();

  try {
    if (audioContext.state === 'suspended') await audioContext.resume();
    recorder.start(CHUNK_INTERVAL_MS);
    source.start();
    await finished;
    await sendChain;
    if (sendError) throw sendError instanceof Error ? sendError : new Error('Failed to send audio');
  } finally {
    source.disconnect();
    await audioContext.close();
  }
}
